import { css, cx } from '@emotion/css';
import { useEffect, useRef, useState } from 'react';

export function useScrollShadow(dependency: unknown, theme = 'light', bgColor = '#fff') {
  const scrollableRef = useRef<HTMLDivElement>(null);

  const [shouldShowTopShadow, setShowTopShadow] = useState(false);
  const [shouldShowBottomShadow, setShowBottomShadow] = useState(true);
  const [hasSufficientSpace, setHasSufficientSpace] = useState(false);

  useEffect(() => {
    const scrollableElement = scrollableRef.current;

    const handleScroll = () => {
      if (!scrollableElement) return;

      const { scrollTop, scrollHeight, clientHeight } = scrollableElement;

      setShowTopShadow(scrollTop > 0);
      // The scroll sometimes does not bottom out full and lands on a decimal value so we ceil
      setShowBottomShadow(Math.ceil(scrollTop) < scrollHeight - clientHeight);
      // Check if there's sufficient space (e.g., clientHeight > 300px threshold)
      setHasSufficientSpace(clientHeight > 300);
    };

    handleScroll(); // To avoid first render glitch when content is not scrollable

    scrollableElement?.addEventListener('scroll', handleScroll);

    return () => {
      scrollableElement?.removeEventListener('scroll', handleScroll);
    };
  }, [dependency]);

  const shadowHeight = hasSufficientSpace
    ? 'var(--mantine-spacing-space10)'
    : 'var(--mantine-spacing-xs)';

  const topShadow = (
    <div
      className={cx(styles(bgColor, shadowHeight).shadow.base, {
        [styles().shadow.hidden]: !shouldShowTopShadow,
        [styles().shadow.darkShadow]: theme === 'dark',
      })}
    />
  );

  const bottomShadow = (
    <div
      className={cx(styles(bgColor, shadowHeight).shadow.base, styles().shadow.bottom, {
        [styles().shadow.hidden]: !shouldShowBottomShadow,
        [styles().shadow.darkShadow]: theme === 'dark',
      })}
    />
  );

  return { topShadow, bottomShadow, scrollableRef, hasReachedBottom: !shouldShowBottomShadow };
}

const styles = (bgColor?: string, shadowHeight?: string) => ({
  shadow: {
    base: css`
      position: absolute;
      top: 0;
      left: 0;
      z-index: 1;
      width: 100%;
      height: ${shadowHeight};
      background: linear-gradient(180deg, ${bgColor} 20%, rgba(255, 255, 255, 0.5), transparent);
      transition: opacity 0.3s;
      pointer-events: none;
    `,
    darkShadow: css`
      background: linear-gradient(180deg, rgba(200, 200, 200, 0.3), transparent);
    `,
    bottom: css`
      top: unset;
      bottom: 0;
      transform: rotate(180deg);
    `,
    hidden: css`
      opacity: 0;
    `,
  },
});
